<!--
 * @Author: Nemo
 * @Description: 支付选项选择器组件
 * @Date: 2024-07-10
 * @LastEditors: Nemo
-->
<template>
  <div class="payment-option-selector" :class="customClass">
    <div class="options-wrap" :style="gridStyle">
      <div
        v-for="(item, index) in displayOptions"
        :key="index"
        class="option-item"
        :class="{'option-active': selectedCode === item.code}"
        :style="[
          selectedCode === item.code ? { '--active-border-color': activeBorderColor } : {},
          itemStyle
        ]"
        @click="handleSelect(item.code)"
      >
        <div class="option-img-wrap">
          <img :src="item.image" :alt="item.name || item.code">
        </div>
        <span v-if="showName && item.name" class="option-name">{{ item.name }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import optionsConfig from './config.js';

export default {
  name: 'OnPaymentOptionSelector',
  props: {
    // 卡种类型
    cardType: {
      type: String,
      required: true,
    },
    // 自定义选项，优先级高于配置
    options: {
      type: Array,
      default: null,
    },
    // 当前选中的代码
    selectedCode: {
      type: String,
      default: '',
    },
    // 自定义类名
    customClass: {
      type: String,
      default: '',
    },
    // 每行列数
    columns: {
      type: Number,
      default: 4, // 默认一行4个
    },
    // 是否显示名称
    showName: {
      type: Boolean,
      default: true,
    },
    // 项目宽度
    itemWidth: {
      type: [Number, String],
      default: 120, // 默认120px宽度
    },
    // 项目高度
    itemHeight: {
      type: [Number, String],
      default: 80, // 默认80px高度
    },
    // 是否强制每行列数（不自动响应式）
    forceColumns: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters(['app']),
    /**
     * 获取主题色
     * 优先使用app.brandInfo.color，如果不存在则使用$color-text-primary
     * @returns {String} 主题色值
     */
    themeColor() {
      return this.app && this.app.brandInfo && this.app.brandInfo.color
        ? this.app.brandInfo.color
        : '';
    },
    /**
     * 获取选中项的边框颜色
     * 优先使用themeColor，否则返回空（将使用CSS中的$color-purple-primary）
     */
    activeBorderColor() {
      return this.themeColor || '';
    },
    // 获取显示选项
    displayOptions() {
      // 如果提供了自定义选项，优先使用
      if (this.options && this.options.length) {
        return this.options;
      }

      // 否则从配置中获取
      return optionsConfig[this.cardType] || [];
    },
    // 计算网格样式
    gridStyle() {
      const width = typeof this.itemWidth === 'number' ? `${this.itemWidth}px` : this.itemWidth;

      // 如果每行只有一个选项或强制列数，则使用固定列数
      if (this.columns === 1 || this.forceColumns) {
        return {
          'grid-template-columns': `repeat(${this.columns}, 1fr)`,
          'grid-gap': '10px',
        };
      }

      // 否则使用响应式布局，根据容器宽度自动调整
      return {
        'grid-template-columns': `repeat(auto-fit, minmax(${width}, 1fr))`,
        'grid-gap': '10px',
      };
    },
    // 计算子项样式
    itemStyle() {
      const width = typeof this.itemWidth === 'number' ? `${this.itemWidth}px` : this.itemWidth;
      const height = typeof this.itemHeight === 'number' ? `${this.itemHeight}px` : this.itemHeight;

      return {
        width,
        height,
        'min-width': width,
        'max-width': '100%',
      };
    },
  },
  methods: {
    handleSelect(code) {
      this.$emit('select', code);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.payment-option-selector {
  width: 100%;

  .options-wrap {
    display: grid;
    gap: 10px;
    padding: 10px;
    justify-items: center;
  }

  .option-item {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 10px;
    border: 1px solid #e8e8e8;
    border-radius: 8px;
    cursor: pointer;
    transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
    background-color: #fff;
    box-sizing: border-box;
    overflow: hidden;

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: transparent;
      opacity: 0;
      transition: opacity 0.2s ease;
      z-index: 1;
      border-radius: 7px;
    }

    &:hover {
      transform: translateY(-2px);
      border-color: #d0d0d0;
      box-shadow: 0 4px 8px rgba(0, 0, 0, 0.08);

      &::before {
        opacity: 0.04;
        background-color: #000;
      }
    }

    &:active {
      transform: scale(0.98);
      transition: transform 0.1s ease;
    }

    .option-img-wrap {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 40px;
      margin-bottom: 5px;
      z-index: 2;

      img {
        max-width: 100%;
        height: auto;
        max-height: 40px;
        object-fit: contain;
        transition: transform 0.2s ease;
      }
    }

    .option-name {
      margin-top: 5px;
      font-size: 12px;
      text-align: center;
      color: #333;
      z-index: 2;
    }
  }

  .option-active {
    border-color: var(--active-border-color, $color-purple-primary);
    border-width: 2px;
    box-shadow: 0 0 8px rgba(78, 118, 229, 0.25);

    &::before {
      opacity: 0;
      background-color: transparent;
    }

    &:hover {
      border-color: var(--active-border-color, $color-purple-primary);
      box-shadow: 0 4px 12px rgba(78, 118, 229, 0.3);

      &::before {
        opacity: 0.04;
        background-color: #000;
      }
    }
  }
}
</style>
